import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../ui/button';
import { notesApi } from '../../lib/api';
import {
  HIGHLIGHT_COLOURS, colourClass, contextOf, locate, pieces, redactionRanges, selectionIn,
} from '../../lib/highlights';
import { describeMarker } from '../mylife/RedactionAwareText';
import { CommentThread } from './CommentThread';
import { SaveToCollection } from './SaveToCollection';

const Swatches = ({ value, onPick, testId, label = 'Highlight colour' }) => (
  <div className="flex items-center gap-1.5" role="radiogroup" aria-label={label}>
    {HIGHLIGHT_COLOURS.map((c) => (
      <button
        key={c.id}
        type="button"
        role="radio"
        aria-checked={value === c.id}
        aria-label={c.label}
        title={c.label}
        onMouseDown={(e) => e.preventDefault() /* keep the selection */}
        onClick={() => onPick(c.id)}
        className={`w-7 h-7 rounded-md border ${c.className} ${value === c.id ? 'border-ink ring-2 ring-ink/30' : 'border-rule'}`}
        data-testid={`${testId}-${c.id}`}
      />
    ))}
  </div>
);

/**
 * A record's text that the person can highlight, with their highlights,
 * notes on each, and their notes on the record itself.
 *
 * Select words → a bar offers the pastel colours ("Highlight") and
 * "Highlight and add a note". A highlight opens a panel: its words, change
 * colour, notes on it, save it to a collection, remove it. Highlights are
 * found again by their words if the record's text changes after "Analyse
 * again"; one that can't be found is listed, never silently dropped.
 */
// A record can come from several organisations, so notes on it aren't filed
// under one (deleting that organisation would take them while the record
// stays); they're removed when the record itself goes.
export const RecordHighlights = ({ recordId, text, className, readOnlyView = null, testId = 'record-highlights' }) => {
  const ref = useRef(null);
  const [notes, setNotes] = useState(null);
  const [locked, setLocked] = useState(false);
  const [sel, setSel] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await notesApi.list({ target_type: 'record', target_id: recordId });
      if (r.data?.locked) { setLocked(true); setNotes([]); return; }
      const own = r.data?.notes || [];
      const hlIds = own.filter((n) => n.kind === 'highlight').map((n) => n.id);
      const onHighlights = hlIds.length
        ? (await notesApi.list({ target_type: 'highlight', target_ids: hlIds })).data?.notes || []
        : [];
      setNotes([...own, ...onHighlights]);
    } catch {
      setNotes([]);
    }
  }, [recordId]);

  useEffect(() => { setNotes(null); setActiveId(null); setSel(null); load(); }, [load]);

  const highlights = useMemo(() => (notes || []).filter((n) => n.kind === 'highlight'), [notes]);
  const located = useMemo(() => highlights.map((h) => ({ h, at: locate(text || '', h) })), [highlights, text]);
  const shown = located.filter((x) => x.at).map(({ h, at }) => ({ id: h.id, colour: h.colour, start: at.start, end: at.end }));
  const lost = located.filter((x) => !x.at).map((x) => x.h);
  const parts = useMemo(() => pieces(text || '', shown, redactionRanges(text || '')), [text, shown]);
  const active = highlights.find((h) => h.id === activeId) || null;
  const commentsOn = (id) => (notes || []).filter((n) => n.target_type === 'highlight' && n.target_id === id);
  const recordNotes = (notes || []).filter((n) => n.target_type === 'record' && n.kind === 'comment');

  const readSelection = () => setSel(selectionIn(ref.current));

  const highlight = async (colour, thenOpen = false) => {
    if (!sel) return;
    setError(null);
    try {
      const r = await notesApi.create({
        kind: 'highlight', target_type: 'record', target_id: recordId, colour,
        quote: sel.quote, start: sel.start, end: sel.end, ...contextOf(text, sel.start, sel.end),
      });
      window.getSelection()?.removeAllRanges();
      setSel(null);
      setNotes((ns) => [...(ns || []), r.data]);
      if (thenOpen) setActiveId(r.data.id);
    } catch (e) {
      setError(e?.response?.data?.detail || 'That highlight didn’t save — try again.');
    }
  };

  const recolour = async (h, colour) => {
    const r = await notesApi.update(h.id, {
      colour, quote: h.quote, start: h.start, end: h.end, prefix: h.prefix, suffix: h.suffix, text: h.text, stance: h.stance,
    });
    setNotes((ns) => ns.map((n) => (n.id === h.id ? { ...n, ...r.data } : n)));
  };

  const removeHighlight = async (h) => {
    await notesApi.remove(h.id);
    setActiveId(null);
    setNotes((ns) => ns.filter((n) => n.id !== h.id && !(n.target_type === 'highlight' && n.target_id === h.id)));
  };

  if (!text) return <p className="text-ink-55">No text has been read from this record yet.</p>;

  return (
    <div className="space-y-5" data-testid={testId}>
      {readOnlyView ? readOnlyView : (
      <>
      <div className="sticky top-0 z-10 -mx-1 px-1 py-2 bg-surface/95 min-h-[44px] flex items-center gap-3 flex-wrap" aria-live="polite">
        {sel && !locked ? (
          <>
            <span className="text-sm text-ink-70">Highlight</span>
            <Swatches value={null} onPick={(c) => highlight(c)} testId={`${testId}-swatch`} />
            <Button size="sm" variant="secondary" onMouseDown={(e) => e.preventDefault()} onClick={() => highlight('yellow', true)} data-testid={`${testId}-highlight-note`}>
              Highlight and add a note
            </Button>
          </>
        ) : (
          <span className="text-sm text-ink-55">
            {locked ? 'Unlock your account to highlight.' : 'Select any words to highlight them or add a note.'}
          </span>
        )}
      </div>
      {error && <p className="text-sm text-breach-ink">{error}</p>}

      <div
        ref={ref}
        className={className}
        onMouseUp={readSelection}
        onKeyUp={readSelection}
        onTouchEnd={() => setTimeout(readSelection, 0)}
        data-testid={`${testId}-text`}
      >
        {parts.map((p) => {
          if (p.redaction && !p.colour) {
            return <span key={p.start} className="pd-mark-redacted font-mono text-[15px]" aria-label={describeMarker(p.text)}>{p.text}</span>;
          }
          if (p.colour) {
            const id = p.ids[p.ids.length - 1];
            return (
              <mark
                key={p.start}
                className={`${colourClass(p.colour)} text-ink rounded-[3px] cursor-pointer ${p.redaction ? 'font-mono text-[15px]' : ''} ${activeId === id ? 'ring-2 ring-ink/40' : ''}`}
                onClick={() => setActiveId(id)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActiveId(id); } }}
                tabIndex={0}
                role="button"
                aria-label="Your highlight — open it"
                data-testid={`${testId}-mark`}
              >
                {p.text}
              </mark>
            );
          }
          return <span key={p.start}>{p.text}</span>;
        })}
      </div>
      </>
      )}

      {active && (
        <section className="rounded-lg border border-rule bg-surface p-4 space-y-4" aria-label="Your highlight" data-testid={`${testId}-panel`}>
          <div className="flex items-start justify-between gap-3">
            <blockquote className={`${colourClass(active.colour)} rounded-md px-3 py-2 text-[16px] text-ink leading-relaxed`}>“{active.quote}”</blockquote>
            <button type="button" className="text-sm text-ink-55 hover:text-ink shrink-0" onClick={() => setActiveId(null)}>Close</button>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <Swatches value={active.colour} onPick={(c) => recolour(active, c)} testId={`${testId}-recolour`} label="Change the colour" />
            <SaveToCollection targetType="highlight" targetId={active.id} testId={`${testId}-save`} />
            <Button size="sm" variant="ghost" onClick={() => removeHighlight(active)} data-testid={`${testId}-remove`}>Remove highlight</Button>
          </div>
          <CommentThread
            targetType="highlight" targetId={active.id} tone="ground"
            heading="Comments on this passage"
            prompt="Add what you remember, what was really happening, or say if it isn’t true."
            comments={commentsOn(active.id)} onChanged={load} testId={`${testId}-notes`}
          />
        </section>
      )}

      {(highlights.length > 0) && (
        <section className="space-y-2">
          <p className="pd-eyebrow">Your highlights ({highlights.length})</p>
          <ul className="space-y-2">
            {located.filter((x) => x.at).map(({ h }) => (
              <li key={h.id}>
                <button
                  type="button"
                  onClick={() => setActiveId(h.id)}
                  className="w-full text-left rounded-md hover:bg-ground px-2 py-1.5 flex items-start gap-3"
                  data-testid={`${testId}-list-item`}
                >
                  <span className={`${colourClass(h.colour)} mt-1.5 w-3 h-3 rounded-sm shrink-0`} aria-hidden="true" />
                  <span className="text-[15px] text-ink line-clamp-2">“{h.quote}”</span>
                  {commentsOn(h.id).length > 0 && (
                    <span className="ml-auto text-sm text-ink-55 shrink-0">{commentsOn(h.id).length} comment{commentsOn(h.id).length === 1 ? '' : 's'}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
          {lost.length > 0 && (
            <div className="rounded-lg bg-ground p-3 space-y-2" data-testid={`${testId}-lost`}>
              <p className="text-sm text-ink-70">
                We can’t find {lost.length === 1 ? 'this passage' : 'these passages'} in the record’s text any more — it may have changed when the document was analysed again. Your highlight and notes are kept.
              </p>
              {lost.map((h) => (
                <div key={h.id} className="flex items-start gap-3">
                  <span className={`${colourClass(h.colour)} mt-1.5 w-3 h-3 rounded-sm shrink-0`} aria-hidden="true" />
                  <span className="text-[15px] text-ink">“{h.quote}”</span>
                  <button type="button" className="ml-auto text-sm text-ink-55 hover:text-ink shrink-0" onClick={() => setActiveId(h.id)}>Open</button>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      <CommentThread
        targetType="record" targetId={recordId}
        heading="Comments on this record"
        prompt="Add context, say what you remember, or say if something here isn’t true. It stays beside the record — it never changes it."
        comments={notes ? recordNotes : []} onChanged={load} testId={`${testId}-record-notes`}
      />
    </div>
  );
};

export default RecordHighlights;
